/**
 * New dream (Dreams UX pass 2026-09-06). A title is enough; the rest sits
 * under two named groups — "Helps me plan the journey" (which parts of life,
 * by when, priority) and "Helps me keep you going" (what life looks like when
 * it is true). The form offers what the dream page's Add row offers, and
 * nothing else; the journey itself is planned on the dream.
 */
import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Star } from 'lucide-react'
import { HierarchyCreationForm, type BasicField, type FieldGroup } from '../components/hierarchy/HierarchyCreationForm'
import { DreamService } from '../services/dreamService'
import { useToast } from '../components/ui/Toast'
import { useLifeAreaOptions } from '../hooks/useLifeAreas'
import { LIFE_AREA_OPTIONS, lifeAreaLabel, lifeAreaValue } from '../utils/taskForm'
import { DREAM_PRIORITY_OPTIONS } from '../utils/dreamOptions'
import { dreamFormToPayload, type DreamFormData } from '../utils/dreamForm'

const PLAN_GROUP: FieldGroup = { key: 'plan', title: 'Helps me plan the journey', blurb: 'Which parts of life it is for and, roughly, by when. Skip what you do not know yet.' }
const KEEP_GROUP: FieldGroup = { key: 'keep', title: 'Helps me keep you going', blurb: 'What life looks like when this is true, in your own words.' }

const FIELDS: BasicField[] = [
  { name: 'title', label: 'What is the dream?', type: 'text', required: true, placeholder: 'e.g., Feel strong at fifty · Work I am proud of · A home that is quiet', section: 'basic' },
  {
    name: 'life_areas', label: 'Which parts of life is it for?', type: 'chips', multi: true, options: LIFE_AREA_OPTIONS,
    allowCustom: true, customLabel: 'Another…', customPlaceholder: 'e.g. Side project', customValue: lifeAreaValue, labelOf: lifeAreaLabel,
    helpText: 'Pick one or more; the first is the main one. Name your own if none of these fits.',
    group: PLAN_GROUP, section: 'basic',
  },
  { name: 'target_date', span: 'half', label: 'Get there by', type: 'date', group: PLAN_GROUP, section: 'basic', helpText: 'A season or a year is fine. When it passes, I will ask how it is going.' },
  { name: 'priority', span: 'half', label: 'Priority', type: 'chips', options: DREAM_PRIORITY_OPTIONS, group: PLAN_GROUP, section: 'basic' },
  { name: 'description', label: 'Why it matters', type: 'text', placeholder: 'What life looks like when this is true — one line is plenty', group: KEEP_GROUP, section: 'basic' },
]

export const CreateDream: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const seededTitle = (location.state as { title?: string } | null)?.title || ''
  const [formData, setFormData] = useState<DreamFormData>({ title: seededTitle, priority: 'medium' })
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const dreamService = useMemo(() => new DreamService(), [])

  const picked: string[] = Array.isArray(formData.life_areas) ? formData.life_areas.map(String) : []
  const { options: lifeAreaOptions, remember: rememberLifeArea } = useLifeAreaOptions(picked)
  const fields = useMemo(() => FIELDS.map((f) => (f.name === 'life_areas' ? { ...f, options: lifeAreaOptions } : f)), [lifeAreaOptions])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!String(formData.title || '').trim()) {
      setFormError('Give it a name — that is the one thing I need.')
      return
    }
    setIsLoading(true)
    try {
      const created = await dreamService.createDream(dreamFormToPayload(formData))
      picked.forEach(rememberLifeArea)
      toast.success('Dream added', created.title)
      navigate(created?.id ? `/dreams/${created.id}` : '/dreams')
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add the dream')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <HierarchyCreationForm
      title="New dream"
      description="A name is enough. Which parts of life it is for, by when and the journey there can be added on the dream itself, whenever you know."
      icon={<Star className="h-8 w-8 text-brand" />}
      basicFields={fields}
      advancedFields={[]}
      compactHeader
      formData={formData}
      onFormDataChange={setFormData}
      onSubmit={handleSubmit}
      submitLabel="Save"
      error={formError}
      primaryColor="purple"
      backUrl="/dreams"
      isLoading={isLoading}
    />
  )
}

export default CreateDream
